import { SHORTCUT_PAGE_INFO_FIELDS } from '@@partner:graphql/shortcuts'
import useFetchMorePagination from '@@partner:helpers/hooks/useFetchMorePagination'
import { CourierWorkShift } from '@@partner:types:app'
import { CRUD_Variables, PageInfo } from '@@partner:types:graphql'
import { gql, useQuery } from '@apollo/client'
import { useCallback } from 'react'


export interface AllCourierWorkShiftsQuery {
  allCourierWorkShifts: {
    edges: CourierWorkShift[]
    pageInfo: PageInfo
  }
}


export const GQL_QUERY_ALL_COURIER_WORKSHIFTS = gql`
  query allCourierWorkShifts(
    $filter: NestedFilterInput
    $pager: CommonPagerInput
    $sort: CommonSortInput
  ) {
    allCourierWorkShifts(
      filter: $filter
      sort: $sort
      pager: $pager
    ) {
      edges {
        id
        startedAt
        finishedAt
        plannedStartAt
        plannedFinishAt
        is_finished
        is_started
        status
        vehicle_id
        vehicle {
          id
          type {
            id
            code
            name
          }
          license_plate
          vin
          brand
          model
          year
          color
          capacity_kg
          capacity_m3
          temp_min_c
          temp_max_c
          range_km
          average_speed
          is_active
          comment
        }
        courier {
          id
          first_name
          last_name
          fullName
          phone
          shops {
            id
            name
            address
          }
        }
        files {
          url
        }
        courier_id
        comment
      }
      pageInfo {
        ${SHORTCUT_PAGE_INFO_FIELDS}
      }
    }
  }
`

export const useFetchAllCourierWorkShifts = (variables?: CRUD_Variables, pollInterval?: number) => {
  const { data, loading, error, refetch, fetchMore, ...rest } = useQuery<AllCourierWorkShiftsQuery, CRUD_Variables>(GQL_QUERY_ALL_COURIER_WORKSHIFTS, {
    variables,
    pollInterval
  })

  const courierWorkshifts = data?.allCourierWorkShifts.edges || []

  const updaterFunction = useCallback((cached: AllCourierWorkShiftsQuery, fetched: AllCourierWorkShiftsQuery) => {
    const prev = cached?.allCourierWorkShifts?.edges ?? []
    const curr = fetched?.allCourierWorkShifts?.edges ?? []

    return {
      hasMore: curr.length > 0,
      result: {
        allCourierWorkShifts: {
          ...fetched.allCourierWorkShifts,
          edges: [ ...prev, ...curr ]
        }
      }
    }
  }, [])

  const { hasMore, fetchNextPage, currentPage } = useFetchMorePagination({
    loading,
    page: data?.allCourierWorkShifts.pageInfo.page ?? 1,
    pageVariable: 'pager',
    usePager: false,
    fetchMore,
    updaterFunction,
  })

  return {
    data,
    loading,
    error,
    courierWorkshifts,
    refetch,
    hasMore: hasMore && (!data?.allCourierWorkShifts.pageInfo.lastPage || data?.allCourierWorkShifts.pageInfo.lastPage > currentPage),
    fetchNextPage,
    ...rest
  }
}